import { Button } from "@/components/ui/button";
import { Brain, ChevronsUpDown, Plus } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { PremiumBadge } from "@/components/ui/premium-badge";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguagePath } from "@/utils/language";

interface WorkoutFormActionsProps {
  areAllExpanded: boolean;
  toggleAllDays: () => void;
  onAIDialogOpen: () => void;
  onAddDay: () => void;
  canUseAI?: boolean;
}

export function WorkoutFormActions({
  areAllExpanded,
  toggleAllDays,
  onAIDialogOpen,
  onAddDay,
  canUseAI = true
}: WorkoutFormActionsProps) {
  const { t } = useTranslations("workouts");
  const { language } = useLanguage();
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const navigate = useNavigate();
  const { getPath } = useLanguagePath();

  const handleAIClick = () => {
    if (canUseAI) {
      onAIDialogOpen();
    } else {
      setShowPremiumDialog(true);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="space-x-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleAllDays}
            className="dark:text-white dark:border-gray-600 dark:hover:bg-gray-700"
          >
            <ChevronsUpDown className="h-4 w-4 mr-2" />
            {areAllExpanded ? t("newWorkout.actions.collapseAll") : t("newWorkout.actions.expandAll")}
          </Button>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onAddDay}
            className="dark:text-white dark:border-gray-600 dark:hover:bg-gray-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("newWorkout.days.addDay")}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleAIClick}
            className="relative font-medium bg-flame hover:bg-flame/90 text-white"
          >
            {!canUseAI && (
              <div className="absolute -top-3 -left-2">
                <PremiumBadge />
              </div>
            )}
            <Brain className="h-4 w-4 mr-2" />
            {t("newWorkout.actions.generateWithAI")}
          </Button>
        </div>
      </div>

      <Dialog open={showPremiumDialog} onOpenChange={setShowPremiumDialog}>
        <DialogContent className="sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-amber-500" /> 
              {language === 'pt' ? 'Recurso Premium' : 'Premium Feature'}
            </DialogTitle>
            <DialogDescription>
              {language === 'pt' 
                ? 'A geração de treinos com IA é um recurso exclusivo do plano Premium.'
                : 'AI workout generation is an exclusive feature of the Premium plan.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center my-4">
            <PremiumBadge className="text-lg py-2 px-4" />
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowPremiumDialog(false)}
              className="w-full sm:w-auto"
            >
              {language === 'pt' ? 'Fechar' : 'Close'}
            </Button>
            <Button 
              onClick={() => {
                setShowPremiumDialog(false);
                navigate(getPath('/dashboard/plan'));
              }}
              className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white"
            >
              {language === 'pt' ? 'Fazer Upgrade' : 'Upgrade Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
